import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import MainLayout from '@/components/layout/MainLayout';
import Header from '@/components/layout/Header';
import CorporateFooter from '@/components/layout/CorporateFooter';
import SEO from '@/components/common/SEO';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { buildArticleSchema, buildFaqSchema } from '@/lib/seo';
import { getInsightBySlug, getInsightDisplayTags, insightArticles } from '@/lib/insights';
import { pageLabels } from '@/lib/seo-pages';

export default function InsightArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getInsightBySlug(slug) : null;

  if (!slug || !article) {
    return <Navigate to="/insights" replace />;
  }

  const linkedInsightArticles = article.relatedLinks
    .map((href) => insightArticles.find((item) => item.path === href))
    .filter((item): item is (typeof insightArticles)[number] => Boolean(item));

  const relatedArticles = [
    ...linkedInsightArticles,
    ...insightArticles.filter((item) => item.slug !== article.slug && !linkedInsightArticles.some((linked) => linked.slug === item.slug)),
  ].slice(0, 3);

  const displayTags = getInsightDisplayTags(article);
  const getRelatedLinkLabel = (href: string) => pageLabels[href] || insightArticles.find((item) => item.path === href)?.title || href;

  return (

    <MainLayout>
      <SEO
        title={article.seoTitle}
        description={article.metaDescription}
        keywords={article.primaryKeywords.join(', ')}
        url={article.path}
        schema={[
          buildArticleSchema({
            title: article.title,
            description: article.metaDescription,
            path: article.path,
          }),
          buildFaqSchema(article.faqs),
        ]}
      />

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="container mx-auto max-w-6xl px-4 py-12 md:py-20">
          <div className="mx-auto max-w-4xl">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <Link
                to="/insights"
                className="inline-flex items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to insights
              </Link>

              <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                CanXP AI Insights
              </Badge>
            </div>

            <h1 className="mt-6 text-4xl font-semibold tracking-tight md:text-6xl">{article.title}</h1>

            <p className="mt-6 text-lg leading-8 text-muted-foreground">{article.metaDescription}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {displayTags.map((keyword) => (
                <Badge key={keyword} variant="secondary">
                  {keyword}
                </Badge>
              ))}
            </div>

          </div>

          <section className="mx-auto mt-12 max-w-4xl rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-background p-6 shadow-[0_18px_80px_-50px_rgba(220,38,38,0.8)] md:p-8">
            <div className="space-y-4 text-base leading-8 text-foreground/90 md:text-lg">
              {article.intro.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </section>

          <section className="mx-auto mt-12 grid max-w-6xl gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <article className="min-w-0">
              <Card className="border-border/70 bg-background/80">
                <CardContent className="p-6 md:p-8">
                  <div className="prose prose-lg max-w-none dark:prose-invert prose-headings:scroll-mt-24 prose-headings:font-semibold prose-p:text-muted-foreground prose-li:text-muted-foreground prose-a:font-medium prose-a:no-underline hover:prose-a:underline">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.body}</ReactMarkdown>
                  </div>

                </CardContent>
              </Card>
            </article>

            <aside className="space-y-6">
              <Card className="border-primary/15 bg-card/70">
                <CardContent className="p-6 md:p-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Related CanXP pages</p>
                  <div className="mt-6 space-y-3">
                    {article.relatedLinks.map((href) => (
                      <Link
                        key={href}
                        to={href}
                        className="flex items-center justify-between rounded-xl border border-border/70 bg-background/80 px-4 py-3 text-sm font-medium transition-colors hover:border-primary/30 hover:text-primary"
                      >
                        <span>{getRelatedLinkLabel(href)}</span>
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ))}

                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/20 bg-gradient-to-br from-primary/10 via-background to-background shadow-[0_18px_80px_-50px_rgba(220,38,38,0.8)]">
                <CardContent className="p-6 md:p-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Call to action</p>
                  <p className="mt-4 text-sm leading-7 text-muted-foreground">{article.cta}</p>
                  <Button asChild className="mt-6 gap-2">
                    <Link to={article.ctaHref}>
                      {article.ctaLabel}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </aside>
          </section>

          <Separator className="mx-auto my-14 max-w-6xl" />

          <section className="mx-auto max-w-6xl grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-6 md:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Frequently asked questions</p>
                <h2 className="mt-4 text-2xl font-semibold tracking-tight md:text-3xl">Questions readers often ask</h2>
                <Accordion type="single" collapsible className="mt-6 w-full">
                  {article.faqs.map((faq, index) => (
                    <AccordionItem key={faq.question} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                      <AccordionContent className="text-sm leading-7 text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>

            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-6 md:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">More insights</p>
                <div className="mt-6 space-y-4">
                  {relatedArticles.map((item) => (
                    <Link
                      key={item.slug}
                      to={item.path}
                      className="block rounded-2xl border border-border/70 bg-background/80 p-4 transition-colors hover:border-primary/30"
                    >
                      <div className="text-sm font-semibold text-foreground">{item.title}</div>
                      <div className="mt-2 text-sm leading-6 text-muted-foreground">{item.metaDescription}</div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        </main>

        <CorporateFooter />
      </div>
    </MainLayout>
  );
}
